import { useQuery } from '@tanstack/react-query';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { CompoundEntityRef, Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export const SUB_DOC_ENTITY_QUERY_KEY = 'subDocEntity' as const;

// The annotations of a doc entity whose page lives in the docs site of another
// entity. The software-catalogue writes them for each ADR and component
// playbook: the parent ref, and the page path (the file path below `docs/`
// without `.md`, which is also the mkdocs URL path).
const SITE_ENTITY_FILTER_KEY =
  'metadata.annotations.backstage.io/techdocs-entity';
const SITE_PATH_FILTER_KEY =
  'metadata.annotations.backstage.io/techdocs-entity-path';

/**
 * The doc entity of a page inside the docs site of another entity, for
 * example an ADR in the site of its parent component. `null` when no doc
 * entity, or more than 1, points to the page.
 */
export function useSubDocEntity(
  siteEntityRef: CompoundEntityRef,
  pagePath: string,
  { enabled }: { enabled: boolean },
) {
  const catalogApi = useApi(catalogApiRef);
  const siteRef = stringifyEntityRef(siteEntityRef);
  const path = pagePath.replace(/^\/+|\/+$/g, '');

  return useQuery({
    queryKey: [SUB_DOC_ENTITY_QUERY_KEY, siteRef, path],
    queryFn: async (): Promise<Entity | null> => {
      const { items } = await catalogApi.getEntities({
        filter: {
          [SITE_ENTITY_FILTER_KEY]: siteRef,
          [SITE_PATH_FILTER_KEY]: path,
        },
      });
      return items.length === 1 ? items[0] : null;
    },
    enabled,
  });
}
